/// <reference path="../../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// Shared in-bubble game card (types-and-schemas §2.9). Renders a
// compact summary of a game session row — kind glyph + title + phase
// status — and is tappable to open the full game (the host wires
// `onOpen`, mounting the platform game host keyed on `sessionId`).
// Interactive play for coinflip/dice/rps and full board games lives in
// the per-platform game hosts; this card is the timeline entry.

import { Pressable, Text, View } from "react-native";
import { useColorScheme } from '../../foundations/colorScheme';
import { Car, Coins, Crown, Dices, Hand, Spade } from "lucide-react-native";
import type { LucideIcon } from "lucide-react-native";
import { createTheme } from "../../design-system/theme";
import type { GameKind } from "../../appEvent/types";

const GAME_META: Record<GameKind, { icon: LucideIcon; label: string }> = {
	coinflip: { icon: Coins, label: "Coin Flip" },
	dice: { icon: Dices, label: "Dice Roll" },
	rps: { icon: Hand, label: "Rock Paper Scissors" },
	chess: { icon: Crown, label: "Chess" },
	blackjack: { icon: Spade, label: "Blackjack" },
	racing: { icon: Car, label: "Racing" },
};

export interface GameMessageCardProps {
	readonly gameKind: GameKind;
	readonly phase: "create" | "move" | "outcome";
	readonly isSelf: boolean;
	readonly outcomeLabel?: string | undefined;
	readonly onOpen?: (() => void) | undefined;
}

export function GameMessageCard({
	gameKind,
	phase,
	isSelf,
	outcomeLabel,
	onOpen,
}: GameMessageCardProps): JSX.Element {
	const scheme = useColorScheme();
	const theme = createTheme(scheme === "dark" ? "dark" : "light");
	const meta = GAME_META[gameKind];
	const Icon = meta.icon;
	const status =
		phase === "outcome"
			? (outcomeLabel ?? "Game finished")
			: phase === "move"
				? "It's your move"
				: "Started a game";

	return (
		<Pressable
			accessibilityRole="button"
			accessibilityLabel={`${meta.label} game — ${status}`}
			onPress={onOpen}
			className="flex flex-row items-center gap-3 min-w-[220px] max-w-[300px] rounded-2xl p-3"
			style={{
				backgroundColor: isSelf
					? theme.colors.accent.brand
					: theme.colors.surface.elevated,
			}}
		>
			<View
				className="w-10 h-10 rounded-full items-center justify-center"
				style={{ backgroundColor: "rgba(255,255,255,0.15)" }}
			>
				<Icon size={22} strokeWidth={2} color={isSelf ? "#FFFFFF" : theme.colors.text.primary} />
			</View>
			<View className="flex-1 min-w-0">
				<Text
					numberOfLines={1}
					className="font-sans text-[15px] font-bold leading-5"
					style={{ color: isSelf ? "#FFFFFF" : theme.colors.text.primary }}
				>
					{meta.label}
				</Text>
				<Text
					numberOfLines={1}
					className="font-sans text-[13px] leading-[18px]"
					style={{
						color: isSelf ? "rgba(255,255,255,0.8)" : theme.colors.text.secondary,
					}}
				>
					{status}
				</Text>
			</View>
		</Pressable>
	);
}
